import React, { useState, useCallback, useEffect } from 'react';
import { GroupedTimeEntry, EntryDetail, Project, GroupedEntryState, getGroupKey } from '../../config';
import GroupedEntryCard from '../GroupedEntryCard';

interface EntryGridProps {
  groupedEntries: GroupedTimeEntry[];
  groupedState: GroupedEntryState;
  projects: Project[];
  onGroupAllocate: (batchId: number, projectId: number | null, leaveType?: GroupedTimeEntry['leave_type']) => void;
  onGroupHide: (batchId: number, projectId: number | null) => void;
  onEntryAllocate: (entryId: number) => void;
  onEntryHide: (entryId: number) => void;
  onEntryUpdate: (entryId: number, projectId: number | null, hours: number, isLeaveEntry?: boolean) => void;
  onEntryUndoHide: (entryId: number) => void;
  onGroupToggle: (batchId: number, projectId: number | null, leaveType?: GroupedTimeEntry['leave_type']) => void;
  onFetchEntryDetails: (batchId: number, projectId: number | null, leaveType?: GroupedTimeEntry['leave_type']) => Promise<EntryDetail[]>;
}

const EntryGrid: React.FC<EntryGridProps> = ({
  groupedEntries,
  groupedState,
  projects,
  onGroupAllocate,
  onGroupHide,
  onEntryAllocate,
  onEntryHide,
  onEntryUpdate,
  onEntryUndoHide,
  onGroupToggle,
  onFetchEntryDetails
}) => {
  // State for managing entry details for each group
  const [entryDetailsCache, setEntryDetailsCache] = useState<Record<string, EntryDetail[]>>({});

  // Effect to refetch entry details for expanded groups when groupedEntries change
  useEffect(() => {
    const refetchExpandedGroups = async () => {
      const expandedGroupKeys = Array.from(groupedState.expandedGroups);
      
      for (const groupKey of expandedGroupKeys) {
        // Find the corresponding group entry for this key
        const matchingGroup = groupedEntries.find(group => 
          getGroupKey(group.batch_id, group.project_id, group.leave_type) === groupKey
        );
        
        if (matchingGroup) {
          try {
            const details = await onFetchEntryDetails(
              matchingGroup.batch_id, 
              matchingGroup.project_id, 
              matchingGroup.leave_type
            );
            setEntryDetailsCache(prev => ({
              ...prev,
              [groupKey]: details
            }));
          } catch (err) {
            console.error('Error refetching entry details:', err);
            setEntryDetailsCache(prev => ({
              ...prev,
              [groupKey]: []
            }));
          }
        } else {
          // Remove cache for groups that no longer exist
          setEntryDetailsCache(prev => {
            const newCache = { ...prev };
            delete newCache[groupKey];
            return newCache;
          });
        }
      }
    };

    if (groupedState.expandedGroups.size > 0) {
      refetchExpandedGroups();
    }
  }, [groupedEntries, groupedState.expandedGroups, onFetchEntryDetails]);

  // Handle fetching entry details for a specific group
  const handleFetchEntryDetails = useCallback(async (batchId: number, projectId: number | null, leaveType?: GroupedTimeEntry['leave_type']) => {
    const groupKey = getGroupKey(batchId, projectId, leaveType);
    
    try {
      const details = await onFetchEntryDetails(batchId, projectId, leaveType);
      setEntryDetailsCache(prev => ({
        ...prev,
        [groupKey]: details
      }));
    } catch (err) {
      console.error('Error fetching entry details:', err);
      // Keep empty array if error, so component doesn't break
      setEntryDetailsCache(prev => ({
        ...prev,
        [groupKey]: []
      }));
    }
  }, [onFetchEntryDetails]);

  // Handle group allocation wrapper
  const handleGroupAllocate = useCallback((batchId: number, projectId: number | null, leaveType?: GroupedTimeEntry['leave_type']) => {
    onGroupAllocate(batchId, projectId, leaveType);
    
    // Clear cached details after allocation to force refresh
    const groupKey = getGroupKey(batchId, projectId, leaveType);
    setEntryDetailsCache(prev => {
      const newCache = { ...prev };
      delete newCache[groupKey];
      return newCache;
    });
  }, [onGroupAllocate]);

  // Handle entry hide wrapper
  const handleEntryHide = useCallback(async (entryId: number) => {
    // Update the status immediately in cache to provide instant feedback
    setEntryDetailsCache(prev => {
      const newCache = { ...prev };
      Object.keys(newCache).forEach(groupKey => {
        newCache[groupKey] = newCache[groupKey].map(entry => 
          entry.entry_id === entryId 
            ? { ...entry, status: 'Hidden' as const }
            : entry
        );
      });
      return newCache;
    });

    // The useEffect will automatically refetch entry details when groupedEntries change
    await onEntryHide(entryId);
  }, [onEntryHide]);

  // Handle entry update wrapper
  const handleEntryUpdate = useCallback(async (entryId: number, projectId: number | null, hours: number, isLeaveEntry?: boolean) => {
    // Update the entry immediately in cache to provide instant feedback
    setEntryDetailsCache(prev => {
      const newCache = { ...prev };
      Object.keys(newCache).forEach(groupKey => {
        newCache[groupKey] = newCache[groupKey].map(entry => 
          entry.entry_id === entryId 
            ? { ...entry, project_id: projectId, hours: hours }
            : entry
        );
      });
      return newCache;
    });

    // The useEffect will automatically refetch entry details when groupedEntries change
    await onEntryUpdate(entryId, projectId, hours, isLeaveEntry);
  }, [onEntryUpdate]);

  // Handle entry allocation wrapper
  const handleEntryAllocate = useCallback(async (entryId: number) => {
    // Update the status immediately in cache to provide instant feedback
    setEntryDetailsCache(prev => {
      const newCache = { ...prev };
      Object.keys(newCache).forEach(groupKey => {
        newCache[groupKey] = newCache[groupKey].map(entry => 
          entry.entry_id === entryId 
            ? { ...entry, status: 'Allocated' as const }
            : entry
        );
      });
      return newCache;
    });

    // The useEffect will automatically refetch entry details when groupedEntries change
    await onEntryAllocate(entryId);
  }, [onEntryAllocate]);

  // Handle entry undo hide wrapper
  const handleEntryUndoHide = useCallback(async (entryId: number) => {
    // Update the status immediately in cache to provide instant feedback
    setEntryDetailsCache(prev => {
      const newCache = { ...prev };
      Object.keys(newCache).forEach(groupKey => {
        newCache[groupKey] = newCache[groupKey].map(entry => 
          entry.entry_id === entryId 
            ? { ...entry, status: 'Submitted' as const }
            : entry
        );
      });
      return newCache;
    });

    // The useEffect will automatically refetch entry details when groupedEntries change
    await onEntryUndoHide(entryId);
  }, [onEntryUndoHide]);

  if (groupedEntries.length === 0) {
    return (
      <div className="entry-grid-empty">
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>No timesheet entries found</h3>
          <p>Try adjusting your filters to see more entries.</p>
        </div>
      </div>
    );
  }

  // Sort grouped entries by week ending date (newest first) and then by project name
  const sortedGroupedEntries = [...groupedEntries].sort((a, b) => {
    const dateComparison = new Date(b.week_ending_date).getTime() - new Date(a.week_ending_date).getTime();
    if (dateComparison !== 0) return dateComparison;
    
    // If same week, sort by project name
    return a.project_name.localeCompare(b.project_name);
  });

  // Calculate totals for summary
  const totalEntries = groupedEntries.reduce((sum, group) => sum + group.entry_count, 0);
  const totalHours = groupedEntries.reduce((sum, group) => sum + group.total_hours, 0);
  const totalProjects = new Set(groupedEntries.map(group => group.project_id)).size;

  return (
    <div className="entry-grid">
      <div className="entry-grid-header">
        <div className="header-info">
          <div className="summary-stats">
            <span className="stat">
              <strong>{groupedEntries.length}</strong> project groups
            </span>
            <span className="stat">
              <strong>{totalEntries}</strong> total entries
            </span>
            <span className="stat">
              <strong>{totalHours.toFixed(2)}</strong> total hours
            </span>
            <span className="stat">
              <strong>{totalProjects}</strong> unique projects
            </span>
          </div>
        </div>

        <div className="grid-controls">
          <div className="expand-controls">
            <button 
              className="expand-all-btn"
              onClick={() => {
                // Expand all groups
                groupedEntries.forEach(group => {
                  const groupKey = getGroupKey(group.batch_id, group.project_id, group.leave_type);
                  if (!groupedState.expandedGroups.has(groupKey)) {
                    onGroupToggle(group.batch_id, group.project_id, group.leave_type);
                  }
                });
              }}
              disabled={groupedEntries.every(group => 
                groupedState.expandedGroups.has(getGroupKey(group.batch_id, group.project_id, group.leave_type))
              )}
            >
              Expand All
            </button>
            
            <button 
              className="collapse-all-btn"
              onClick={() => {
                // Collapse all groups
                groupedEntries.forEach(group => {
                  const groupKey = getGroupKey(group.batch_id, group.project_id, group.leave_type);
                  if (groupedState.expandedGroups.has(groupKey)) {
                    onGroupToggle(group.batch_id, group.project_id, group.leave_type);
                  }
                });
              }}
              disabled={groupedEntries.every(group => 
                !groupedState.expandedGroups.has(getGroupKey(group.batch_id, group.project_id, group.leave_type))
              )}
            >
              Collapse All
            </button>
          </div>
        </div>
      </div>

      <div className="entry-grid-content">
        <div className="grouped-entry-cards-list">
          {sortedGroupedEntries.map(groupedEntry => {
            const groupKey = getGroupKey(groupedEntry.batch_id, groupedEntry.project_id, groupedEntry.leave_type);
            const isExpanded = groupedState.expandedGroups.has(groupKey);
            const entryDetails = entryDetailsCache[groupKey] || [];

            return (
              <GroupedEntryCard
                key={groupKey}
                groupedEntry={groupedEntry}
                isExpanded={isExpanded}
                entryDetails={entryDetails}
                projects={projects}
                onToggleExpanded={() => onGroupToggle(groupedEntry.batch_id, groupedEntry.project_id, groupedEntry.leave_type)}
                onGroupAllocate={() => handleGroupAllocate(groupedEntry.batch_id, groupedEntry.project_id, groupedEntry.leave_type)}
                onEntryHide={handleEntryHide}
                onEntryUpdate={handleEntryUpdate}
                onEntryAllocate={handleEntryAllocate}
                onEntryUndoHide={handleEntryUndoHide}
                onFetchDetails={() => handleFetchEntryDetails(groupedEntry.batch_id, groupedEntry.project_id, groupedEntry.leave_type)}
              />
            );
          })}
        </div>
      </div>


    </div>
  );
};

export default EntryGrid; 